'use client'

import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Input } from '@/shared/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from '@/shared/components/ui/popover'
import { useIsMobile } from '@/shared/hooks/use-mobile'
import { X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { getDisplayEmailValue } from '@/features/volunteers/schemas/volunteers.schema'
import { authFetchJson } from '@/shared/lib/auth-fetch'

export type VolunteerContact = {
  id: number
  fullname: string
  email?: string
  phone?: string
  organizationName?: string
  dietaryRestrictions?: string | null
}

const SUGGESTIONS_MAX = 50

type ContactUserSelectProps = {
  value: number | null
  onChange: (userId: number | null) => void
  /** Called when a user is selected (e.g. to populate contact_phone from user.phone) */
  onVolunteerSelect?: (v: VolunteerContact) => void
  /** Called when user clicks "Add as new contact" (when no matches and name is typed) */
  onAddNewContact?: (name: string) => void
  /** Called when the displayed/typed value in the input changes (so parent can sync to form for auto-create on submit) */
  onDisplayValueChange?: (value: string) => void
  /** When true, show "No ML Approved" option and allow value === -1 */
  allowNoMlApproved?: boolean
  /** Optional generic "no selection" row label (e.g. "No GL"). Selecting it clears value to null. */
  noSelectionOptionLabel?: string
  /** Display string when a user is selected (e.g. from group.contact_name when editing) */
  displayValue?: string
  /** When true and value is null, still render displayValue (useful for persisted name while id resolution catches up). */
  showDisplayValueWhenNoValue?: boolean
  placeholder?: string
  disabled?: boolean
}

export const NO_ML_APPROVED_VALUE = -1

/**
 * Select-only contact picker. Only allows choosing a ReliefHub user from the database.
 * No free-text name or email - contact must be an existing user.
 */
export function ContactUserSelect({
  value,
  onChange,
  onVolunteerSelect,
  onAddNewContact,
  onDisplayValueChange,
  allowNoMlApproved,
  noSelectionOptionLabel,
  displayValue,
  showDisplayValueWhenNoValue = false,
  placeholder = 'Select contact...',
  disabled = false,
}: ContactUserSelectProps) {
  const [isListOpen, setIsListOpen] = useState(false)
  const [localQuery, setLocalQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const isMobile = useIsMobile()

  // On mobile, when the popover opens the portal can steal focus so the input
  // becomes unfocusable and the user can't type. Refocus the input after open.
  useEffect(() => {
    if (!isListOpen) return
    const t = setTimeout(() => inputRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [isListOpen])

  const { data } = useQuery({
    queryKey: ['volunteers-team-leaders'],
    queryFn: async () => {
      const res = await authFetchJson<any[]>('/user/by-role?roleId=1,2,3,4')
      return res.success && res.data ? res.data : []
    },
  })

  const volunteers: VolunteerContact[] = useMemo(() => {
    if (!Array.isArray(data)) return []
    return data.map((u: { id?: number | string; fullname?: string; email?: string; phone?: string; organizationName?: string }) => ({
      id: Number(u.id ?? 0),
      fullname: u.fullname || 'Unknown',
      email: u.email,
      phone: u.phone,
      organizationName: u.organizationName,
      dietaryRestrictions:
        (u as { volunteer?: { dietaryRestrictions?: string | null } }).volunteer?.dietaryRestrictions ??
        (u as { dietaryRestrictions?: string | null }).dietaryRestrictions ??
        null,
    }))
  }, [data])

  const selectedUser = useMemo(
    () => (value === NO_ML_APPROVED_VALUE ? null : volunteers.find((v) => v.id === value) ?? null),
    [volunteers, value]
  )

  const isNoMlApproved = value === NO_ML_APPROVED_VALUE

  const suggestions = useMemo(() => {
    const q = localQuery.trim().toLowerCase()
    const matches = q
      ? volunteers.filter((v) => v.fullname.toLowerCase().includes(q)).slice(0, SUGGESTIONS_MAX)
      : volunteers.slice(0, SUGGESTIONS_MAX)
    return matches
  }, [volunteers, localQuery])

  const showList = isListOpen && !disabled
  const showNoMlOption = Boolean(allowNoMlApproved)
  const showNoSelectionOption = Boolean(noSelectionOptionLabel && noSelectionOptionLabel.trim().length > 0)

  const displayText =
    localQuery !== ''
      ? localQuery
      : isNoMlApproved
        ? 'No ML Approved'
        : value != null
          ? (selectedUser?.fullname ?? displayValue ?? '')
          : (showDisplayValueWhenNoValue ? (displayValue ?? '') : '')
  // When value is null we show empty (not displayValue) so the user can clear and type a new choice
  const isNoSelectionDisplayed =
    value == null &&
    showNoSelectionOption &&
    localQuery === '' &&
    displayText === (noSelectionOptionLabel ?? '')
  const canShowClearButton = !disabled && (value != null || isNoSelectionDisplayed)

  const handleInputChange = (v: string) => {
    setLocalQuery(v)
    setIsListOpen(true)
    onDisplayValueChange?.(v)
    // When user clears the input by backspacing, clear the selection so the
    // field stays empty instead of reverting to the selected name (fixes mobile).
    if (v === '') {
      onChange(null)
    }
  }

  const handleSelect = (v: VolunteerContact) => {
    onChange(Number(v.id))
    onVolunteerSelect?.(v)
    setLocalQuery('')
    setIsListOpen(false)
  }

  const handleClear = () => {
    onChange(null)
    onDisplayValueChange?.('')
    setLocalQuery('')
    setIsListOpen(false)
  }

  return (
    <Popover open={showList} onOpenChange={(open) => !disabled && setIsListOpen(open)} modal={false}>
      <PopoverAnchor asChild>
        <div className="relative flex items-center">
          <Input
            ref={inputRef}
            placeholder={placeholder}
            value={displayText}
            onChange={(e) => {
              if (disabled) return
              const v = (e.target as HTMLInputElement).value
              handleInputChange(v)
            }}
            onClick={() => !disabled && setIsListOpen(true)}
            readOnly={disabled}
            disabled={disabled}
            className={cn(canShowClearButton ? 'pr-9' : undefined, disabled && 'bg-muted cursor-not-allowed')}
          />
          {canShowClearButton && (
            <button
              type="button"
              aria-label="Clear selection"
              className={cn(
                'absolute right-2 top-1/2 z-10 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent',
                'touch-manipulation'
              )}
              onPointerDown={(e) => {
                e.preventDefault()
                handleClear()
              }}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        side={isMobile ? 'top' : 'bottom'}
        className="w-[var(--radix-popover-trigger-width)] p-0 max-h-[40vh] sm:max-h-48"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="max-h-[40vh] sm:max-h-48 overflow-y-auto">
          {value != null && (
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent focus:bg-accent focus:outline-none border-b"
              onPointerDown={(e) => {
                e.preventDefault()
                handleClear()
              }}
            >
              Clear selection
            </button>
          )}
          {allowNoMlApproved && (
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none border-b"
              onPointerDown={(e) => {
                e.preventDefault()
                onChange(NO_ML_APPROVED_VALUE)
                setLocalQuery('')
                setIsListOpen(false)
              }}
            >
              No ML Approved
            </button>
          )}
          {showNoSelectionOption && (
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none border-b"
              onClick={() => {
                onChange(null)
                onDisplayValueChange?.(noSelectionOptionLabel ?? '')
                setLocalQuery('')
                setIsListOpen(false)
              }}
            >
              {noSelectionOptionLabel}
            </button>
          )}
          {suggestions.length === 0 ? (
            <div className="px-3 py-2">
              <div className="text-sm text-muted-foreground text-center py-2">
                {localQuery.trim() ? 'No matching users' : 'No users found'}
              </div>
              {localQuery.trim() && onAddNewContact && (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm font-medium text-primary hover:bg-accent focus:bg-accent focus:outline-none border-t"
                  onPointerDown={(e) => {
                    e.preventDefault()
                    onAddNewContact(localQuery.trim())
                    setLocalQuery('')
                    setIsListOpen(false)
                  }}
                >
                  Add &quot;{localQuery.trim()}&quot; as new contact
                </button>
              )}
            </div>
          ) : (
            suggestions.map((v) => (
              <button
                key={v.id}
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
                onPointerDown={(e) => {
                  e.preventDefault()
                  handleSelect(v)
                }}
              >
                {v.fullname}
                {v.email && (
                  <span className="ml-2 text-muted-foreground text-xs">{getDisplayEmailValue(v.email)}</span>
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
